import type { Board } from "../engine/board";
import { isLightSource } from "../engine/interfaces";
import type { Camera } from "./camera";
import type { EffectManager } from "./effectManager";
import { resolveEntityBoardPosition } from "./particles/entityPositionResolver";

// The darkness layer lives on its own canvas element stacked over the game canvas and
// stretched by CSS, so the browser compositor does the upscale-and-blend on the GPU.
// Blitting it into the game canvas instead costs ~7ms/frame at Retina resolution in
// browsers that rasterize canvas-to-canvas stretch-blends on the CPU (measured in Firefox).
// Composed at 1/SCALE resolution: bilinear filtering on the upscale is indistinguishable
// for a smooth gradient while doing 1/(SCALE^2) as much fill work.
const SCALE = 4;

let overlayCanvas: HTMLCanvasElement | null = null;
let overlayCtx: CanvasRenderingContext2D | null = null;

function ensureOverlay(gameCanvas: HTMLCanvasElement, ow: number, oh: number): CanvasRenderingContext2D {
	if (!overlayCanvas) {
		overlayCanvas = document.createElement("canvas");
		overlayCanvas.className = "darkness-overlay";
		gameCanvas.parentElement!.insertBefore(overlayCanvas, gameCanvas.nextSibling);
		overlayCtx = overlayCanvas.getContext("2d")!;
	}
	overlayCanvas.style.display = "";
	if (overlayCanvas.width !== ow || overlayCanvas.height !== oh) {
		overlayCanvas.width = ow;
		overlayCanvas.height = oh;
	}
	if (overlayCanvas.style.width !== gameCanvas.style.width || overlayCanvas.style.height !== gameCanvas.style.height) {
		overlayCanvas.style.width = gameCanvas.style.width;
		overlayCanvas.style.height = gameCanvas.style.height;
	}
	const octx = overlayCtx!;
	octx.globalCompositeOperation = "source-over";
	octx.clearRect(0, 0, ow, oh);
	return octx;
}

export function hideDarknessOverlay(): void {
	if (overlayCanvas) overlayCanvas.style.display = "none";
}

export function renderDarknessOverlay(
	gameCanvas: HTMLCanvasElement,
	camera: Camera,
	board: Board,
	preEffects: EffectManager,
	posEffects: EffectManager,
): number | null {
	if (board.darkness <= 0 || board.player?.canSeeInDark()) {
		hideDarknessOverlay();
		return null;
	}

	const composeStartMs = performance.now();
	const darkAlpha = Math.min(1, Math.max(0, board.darkness));
	const ow = Math.ceil(gameCanvas.width / SCALE);
	const oh = Math.ceil(gameCanvas.height / SCALE);
	const inv = 1 / SCALE;

	const octx = ensureOverlay(gameCanvas, ow, oh);

	octx.fillStyle = `rgba(0, 0, 0, ${darkAlpha})`;
	octx.fillRect(0, 0, ow, oh);

	octx.globalCompositeOperation = "destination-out";

	const player = board.player;
	if (player) {
		const boardPos = resolveEntityBoardPosition(player, preEffects, posEffects);
		punchLight(
			octx,
			ow,
			oh,
			((boardPos.x + 0.5) * camera.cellWidth - camera.offsetX) * inv,
			((boardPos.y + 0.5) * camera.cellHeight - camera.offsetY) * inv,
			camera.cellWidth * board.lightInnerRadius * inv,
			camera.cellWidth * board.lightOuterRadius * inv,
		);
	}

	for (const thing of board.getPhysicalThings()) {
		if (!isLightSource(thing)) continue;
		const outer = thing.getLightOuterRadius();
		if (outer <= 0) continue;
		const inner = thing.getLightInnerRadius();
		punchLight(
			octx,
			ow,
			oh,
			((thing.xy.x + 0.5) * camera.cellWidth - camera.offsetX) * inv,
			((thing.xy.y + 0.5) * camera.cellHeight - camera.offsetY) * inv,
			camera.cellWidth * inner * inv,
			camera.cellWidth * outer * inv,
		);
	}

	return performance.now() - composeStartMs;
}

function punchLight(
	ctx: CanvasRenderingContext2D,
	canvasWidth: number,
	canvasHeight: number,
	cx: number,
	cy: number,
	innerRadius: number,
	outerRadius: number,
): void {
	const x = cx - outerRadius;
	const y = cy - outerRadius;
	const size = outerRadius * 2;

	if (x + size <= 0 || y + size <= 0 || x >= canvasWidth || y >= canvasHeight) return;

	const gradient = ctx.createRadialGradient(cx, cy, innerRadius, cx, cy, outerRadius);
	gradient.addColorStop(0, "rgba(255, 255, 255, 1)");
	gradient.addColorStop(1, "rgba(255, 255, 255, 0)");
	ctx.fillStyle = gradient;
	ctx.fillRect(x, y, size, size);
}
